import React from 'react';

// EditorialScene — premium illustrated SVG scenes per occasion.
//
// Recovered from the original Claude Design HTML build. Each scene owns its
// palette, composition, and one hero motion (slow + eased). Print-quality
// flat illustration (risograph / cut-paper / gouache feel). The viewBox is
// always 350×200 and the card renders this via slice so the SVG fills
// however wide the card ends up. Amount + occasion + promo overlays come
// from VoucherCard (same pattern as AbstractScene).
//
// All hand-drawn SVG path data and motion classnames preserved verbatim from
// the original EditorialIllustrations.jsx + EditorialIllustrations2.jsx.

// ────────────────────────────────────────────────────────────────────────────
// Shared grain filter — translucent paper-grain wash overlaid on the scene.
// Two variants: dark (for dark grounds) and light (for cream grounds).
// ────────────────────────────────────────────────────────────────────────────
const Grain = ({ id, light = false, strength = 0.22, seed = 4 }) => (
  <filter id={id} x="0%" y="0%" width="100%" height="100%">
    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed={seed} stitchTiles="stitch" />
    {light
      ? <feColorMatrix values={`0 0 0 0 1  0 0 0 0 0.95  0 0 0 0 0.85  0 0 0 ${strength} 0`} />
      : <feColorMatrix values={`0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 ${strength} 0`} />
    }
  </filter>
);

// Blossom helper — 5 petals + center, hand-drawn feel.
const Blossom = ({ x, y, s = 1 }) => {
  const petals = [];
  for (let i = 0; i < 5; i++) {
    const a = (i * 72 - 90) * Math.PI / 180;
    const px = Math.cos(a) * 4.5 * s;
    const py = Math.sin(a) * 4.5 * s;
    petals.push(
      <path key={i}
            d={`M ${px} ${py}
                C ${px + 6 * s * Math.cos(a + 0.6)} ${py + 6 * s * Math.sin(a + 0.6)},
                  ${px + 6 * s * Math.cos(a - 0.6)} ${py + 6 * s * Math.sin(a - 0.6)},
                  ${px} ${py} Z`}
            transform={`translate(${x} ${y}) scale(1.1)`}
            fill="#E9B4BF" opacity="0.95" />
    );
    petals.push(
      <ellipse key={`i${i}`}
               cx={px * 0.7} cy={py * 0.7}
               rx={3.5 * s} ry={2.2 * s}
               transform={`translate(${x} ${y}) rotate(${i * 72 - 90} ${px * 0.7} ${py * 0.7})`}
               fill="#F2CDD1" opacity="0.85" />
    );
  }
  return (
    <g>
      {petals}
      <circle cx={x} cy={y} r={2.5 * s} fill="#B36676" />
      <circle cx={x - 0.8 * s} cy={y - 0.8 * s} r={1 * s} fill="#DA8FA1" />
    </g>
  );
};

// Maple-style leaf — hand-drawn, 5 lobes, asymmetric. Used for Autumn.
const Leaf = ({ x = 0, y = 0, rot = 0, color = "#B85C23", s = 1 }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}>
    <path d="M 0 0
             C -6 -4, -10 -10, -8 -16
             C -5 -14, -2 -16, 0 -20
             C 2 -16, 5 -14, 8 -16
             C 10 -10, 6 -4, 0 0
             C -3 -2, 3 -2, 0 0 Z"
          fill={color} />
    <path d="M 0 0 L 0 -18" stroke="#3A2A1E" strokeWidth="0.6" opacity="0.55" />
    <path d="M 0 -8 L -5 -12 M 0 -8 L 5 -12" stroke="#3A2A1E" strokeWidth="0.5" opacity="0.4" />
    <line x1="0" y1="0" x2="0" y2="4" stroke="#3A2A1E" strokeWidth="0.8" />
  </g>
);

// ────────────────────────────────────────────────────────────────────────────
// BIRTHDAY — round layer cake, single tall candle, anchored flame.
// ────────────────────────────────────────────────────────────────────────────
const EIllBirthday = () => (
  <svg className="eill eill-birthday" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="bd-grain" light seed={3} strength={0.16} /></defs>
    <rect width="350" height="200" fill="#F0CFCA" />
    <ellipse cx="145" cy="178" rx="68" ry="4" fill="#C48A88" opacity="0.55" />
    <g>
      <path d="M 89 178 C 89 176, 90 172, 93 170 L 197 170 C 200 172, 201 176, 201 178 L 201 142 C 201 140, 199 137, 197 136 L 93 136 C 91 137, 89 140, 89 142 Z" fill="#C54962" />
      <path d="M 89 142 C 96 140, 102 144, 109 142 C 116 140, 122 145, 129 142 C 136 140, 142 145, 149 142 C 156 140, 162 145, 169 142 C 176 140, 184 145, 191 142 C 198 140, 201 144, 201 142 L 201 134 L 89 134 Z" fill="#F3E3D0" />
      <path d="M 89 143 C 108 141, 149 144, 201 142" fill="none" stroke="#D8BDA5" strokeWidth="0.7" opacity="0.55" />
      <path d="M 115 134 C 115 132, 117 130, 119 129 L 171 129 C 173 130, 175 132, 175 134 L 175 106 C 175 104, 173 101, 171 100 L 119 100 C 117 101, 115 104, 115 106 Z" fill="#C54962" />
      <path d="M 115 106 C 121 104, 127 108, 133 106 C 139 104, 145 108, 151 106 C 157 104, 163 108, 169 106 C 172 105, 175 107, 175 106 L 175 99 L 115 99 Z" fill="#F3E3D0" />
      <path d="M 115 107 C 133 105, 163 108, 175 106" fill="none" stroke="#D8BDA5" strokeWidth="0.7" opacity="0.55" />
    </g>
    <g>
      <rect x="141" y="50" width="8" height="50" fill="#F3E3D0" />
      <path d="M 141 60 L 149 64" stroke="#C54962" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 141 74 L 149 78" stroke="#C54962" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 141 88 L 149 92" stroke="#C54962" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="145" y1="50" x2="145" y2="42" stroke="#3A2028" strokeWidth="1.6" strokeLinecap="round" />
    </g>
    <g>
      <path d="M 145 42 C 137 37, 137 24, 145 14 C 153 24, 153 37, 145 42 Z" fill="#F2A03E" />
      <path d="M 145 40 C 140 35, 140 26, 145 18 C 150 26, 150 35, 145 40 Z" fill="#F4D168" />
      <ellipse cx="145" cy="32" rx="1.6" ry="4" fill="#FFFFFF" opacity="0.85" />
    </g>
    <rect width="350" height="200" filter="url(#bd-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// MOTHER'S DAY — single stem peony in terracotta vessel, off-center right.
// ────────────────────────────────────────────────────────────────────────────
const EIllMothers = () => (
  <svg className="eill eill-mothers" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="md-grain" light seed={5} strength={0.18} /></defs>
    <rect width="350" height="200" fill="#EADDCA" />
    <line x1="0" y1="168" x2="350" y2="168" stroke="#B89E83" strokeWidth="0.8" opacity="0.5" />
    <g>
      <path d="M 224 168 L 228 142 C 228 138, 234 135, 244 135 L 270 135 C 280 135, 286 138, 286 142 L 290 168 Z" fill="#B85A38" />
      <ellipse cx="257" cy="136" rx="21" ry="2.5" fill="#8B3E21" />
      <path d="M 236 144 C 234 156, 236 164, 238 166" fill="none" stroke="#D07C56" strokeWidth="1.6" strokeLinecap="round" opacity="0.65" />
    </g>
    <path d="M 257 135 C 256 118, 258 96, 256 74" fill="none" stroke="#7A8F5E" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M 257 108 C 242 104, 230 108, 226 118 C 240 118, 252 114, 258 106 Z" fill="#7A8F5E" />
    <path d="M 226 118 C 240 112, 252 108, 258 106" fill="none" stroke="#4F6238" strokeWidth="0.7" opacity="0.6" />
    <g>
      <circle cx="256" cy="58" r="28" fill="#9E2F4C" />
      <path d="M 256 30 C 272 33, 282 45, 282 58 C 274 50, 266 44, 256 40 Z" fill="#8A2540" />
      <path d="M 228 58 C 230 45, 240 33, 254 30 C 246 40, 238 50, 230 58 Z" fill="#8A2540" />
      <path d="M 256 86 C 242 83, 232 73, 230 60 C 238 70, 246 76, 256 80 Z" fill="#8A2540" />
      <path d="M 284 58 C 282 73, 272 83, 258 86 C 264 76, 274 70, 282 60 Z" fill="#8A2540" />
      <circle cx="256" cy="58" r="18" fill="#C04668" />
      <path d="M 256 42 C 264 44, 270 50, 268 58 C 264 54, 260 48, 256 44 Z" fill="#A33654" />
      <path d="M 256 74 C 248 72, 244 64, 246 56 C 250 62, 254 69, 256 74 Z" fill="#A33654" />
      <path d="M 252 52 C 254 48, 258 48, 262 52 C 260 56, 256 58, 252 58 Z" fill="#D97289" />
      <path d="M 256 60 C 260 60, 263 64, 262 68 C 258 66, 254 63, 256 60 Z" fill="#D97289" />
      <circle cx="256" cy="58" r="4" fill="#E8B64A" />
      <circle cx="257" cy="57" r="1.4" fill="#FFF4D6" />
    </g>
    <g className="eill-md-petal">
      <path d="M 0 0 C -5 -2, -5 -9, 0 -12 C 5 -9, 5 -2, 0 0 Z" fill="#C04668" opacity="0.95" />
    </g>
    <rect width="350" height="200" filter="url(#md-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// THANK YOU — envelope with oxblood wax seal, slight tilt.
// ────────────────────────────────────────────────────────────────────────────
const EIllThanks = () => (
  <svg className="eill eill-thanks" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="ty-grain" light seed={9} strength={0.2} /></defs>
    <rect width="350" height="200" fill="#BCC2C9" />
    <g transform="translate(175 108) rotate(-3) translate(-175 -108)">
      <path d="M 98 149 L 252 149 L 252 152 L 98 152 Z" fill="#8E969F" opacity="0.4" />
      <path d="M 100 76 L 250 76 L 250 148 L 100 148 Z" fill="#F4EDDC" />
      <path d="M 100 76 L 175 120 L 250 76 Z" fill="#E5DCC6" />
      <path d="M 100 76 L 175 120 L 250 76" fill="none" stroke="#C8BE9F" strokeWidth="0.8" />
      <path d="M 100 148 L 148 118 M 250 148 L 202 118" stroke="#C8BE9F" strokeWidth="0.8" opacity="0.7" />
      <path d="M 100 76 L 250 76 L 250 148 L 100 148 Z" fill="none" stroke="#C8BE9F" strokeWidth="1" />
      <g className="eill-seal">
        <path d="M 175 98 C 184 98, 191 102, 194 110 C 198 116, 196 124, 193 130 C 190 137, 184 142, 176 143 C 167 144, 159 140, 155 133 C 151 125, 152 116, 156 109 C 160 102, 167 98, 175 98 Z" fill="#A02A38" />
        <path d="M 175 98 C 184 98, 191 102, 194 110 C 198 116, 196 124, 193 130 C 190 137, 184 142, 176 143 C 167 144, 159 140, 155 133 C 151 125, 152 116, 156 109 C 160 102, 167 98, 175 98 Z" fill="none" stroke="#6B1820" strokeWidth="1" opacity="0.75" />
        <ellipse cx="168" cy="112" rx="7" ry="4" fill="#C87585" opacity="0.55" />
        <ellipse cx="184" cy="132" rx="5" ry="2.5" fill="#6B1820" opacity="0.35" />
      </g>
    </g>
    <rect width="350" height="200" filter="url(#ty-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// CHRISTMAS — snowman on snowy berm, distant pines, moon, falling snow.
// ────────────────────────────────────────────────────────────────────────────
const EIllChristmas = () => (
  <svg className="eill eill-christmas" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="xm-grain" seed={11} strength={0.26} />
      <radialGradient id="xm-sky" cx="30%" cy="20%" r="80%">
        <stop offset="0%" stopColor="#2A4A3A" />
        <stop offset="100%" stopColor="#14281F" />
      </radialGradient>
      <radialGradient id="xm-moon" cx="50%" cy="50%" r="50%">
        <stop offset="60%" stopColor="#F3E7C9" stopOpacity="1" />
        <stop offset="100%" stopColor="#F3E7C9" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="350" height="200" fill="url(#xm-sky)" />
    <circle cx="288" cy="48" r="26" fill="url(#xm-moon)" opacity="0.4" />
    <circle cx="288" cy="48" r="16" fill="#F3E7C9" opacity="0.9" />
    <g fill="#F3E7C9" opacity="0.7">
      <circle cx="230" cy="28" r="0.9" />
      <circle cx="252" cy="70" r="0.7" />
      <circle cx="320" cy="80" r="1" />
      <circle cx="340" cy="36" r="0.8" />
      <circle cx="208" cy="56" r="0.8" />
    </g>
    <g fill="#0E1F17" opacity="0.85">
      <path d="M 200 150 L 210 122 L 220 150 Z" />
      <path d="M 218 150 L 230 118 L 242 150 Z" />
      <path d="M 240 150 L 252 126 L 264 150 Z" />
      <path d="M 260 150 L 272 120 L 284 150 Z" />
      <path d="M 282 150 L 294 128 L 306 150 Z" />
      <path d="M 302 150 L 316 122 L 330 150 Z" />
      <path d="M 326 150 L 338 130 L 350 150 Z" />
    </g>
    <path d="M 0 200 L 0 158 C 40 152, 80 160, 140 156 C 200 152, 260 162, 350 156 L 350 200 Z" fill="#F3E7C9" />
    <path d="M 0 162 C 50 158, 110 166, 160 162 C 220 158, 280 168, 350 162" fill="none" stroke="#D4C49E" strokeWidth="1" opacity="0.6" />
    <g>
      <ellipse cx="110" cy="162" rx="36" ry="4" fill="#C4B48E" opacity="0.5" />
      <circle cx="110" cy="140" r="26" fill="#FDF6E3" />
      <circle cx="110" cy="140" r="26" fill="none" stroke="#D4C49E" strokeWidth="0.7" opacity="0.5" />
      <circle cx="110" cy="128" r="1.8" fill="#3A2A22" />
      <circle cx="110" cy="140" r="1.8" fill="#3A2A22" />
      <circle cx="110" cy="152" r="1.8" fill="#3A2A22" />
      <circle cx="110" cy="104" r="18" fill="#FDF6E3" />
      <circle cx="110" cy="104" r="18" fill="none" stroke="#D4C49E" strokeWidth="0.7" opacity="0.5" />
      <path d="M 92 96 C 98 92, 122 92, 128 96 L 128 102 C 122 106, 98 106, 92 102 Z" fill="#A32F3C" />
      <g className="eill-scarf">
        <path d="M 120 100 C 128 108, 132 118, 128 130 L 122 128 C 124 118, 122 110, 116 104 Z" fill="#A32F3C" />
        <line x1="124" y1="128" x2="125" y2="132" stroke="#7A1E28" strokeWidth="0.8" />
        <line x1="127" y1="129" x2="128" y2="133" stroke="#7A1E28" strokeWidth="0.8" />
      </g>
      <circle cx="110" cy="78" r="14" fill="#FDF6E3" />
      <circle cx="110" cy="78" r="14" fill="none" stroke="#D4C49E" strokeWidth="0.7" opacity="0.5" />
      <circle cx="105" cy="76" r="1.4" fill="#2A1E18" />
      <circle cx="115" cy="76" r="1.4" fill="#2A1E18" />
      <circle cx="106" cy="84" r="0.8" fill="#2A1E18" />
      <circle cx="110" cy="85" r="0.8" fill="#2A1E18" />
      <circle cx="114" cy="84" r="0.8" fill="#2A1E18" />
      <path d="M 111 80 L 124 82 L 111 83 Z" fill="#E87F3C" />
      <path d="M 111 80 L 124 82 L 111 83 Z" fill="none" stroke="#B85A1E" strokeWidth="0.4" opacity="0.6" />
      <rect x="98" y="60" width="24" height="4" fill="#1A1410" />
      <rect x="102" y="48" width="16" height="14" fill="#1A1410" />
      <rect x="102" y="58" width="16" height="2.5" fill="#A32F3C" />
      <path d="M 86 108 C 76 106, 70 98, 66 90" fill="none" stroke="#4A2E1E" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 72 96 L 66 92 M 74 100 L 68 100" stroke="#4A2E1E" strokeWidth="1" strokeLinecap="round" />
      <path d="M 134 108 C 144 106, 150 100, 154 94" fill="none" stroke="#4A2E1E" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 148 98 L 154 92 M 146 102 L 152 102" stroke="#4A2E1E" strokeWidth="1" strokeLinecap="round" />
    </g>
    <g className="eill-snow" fill="#F3E7C9">
      <circle className="eill-flake-1" cx="0" cy="0" r="1.4" opacity="0.95" />
      <circle className="eill-flake-2" cx="0" cy="0" r="1" opacity="0.8" />
      <circle className="eill-flake-3" cx="0" cy="0" r="1.2" opacity="0.9" />
      <circle className="eill-flake-4" cx="0" cy="0" r="0.9" opacity="0.75" />
      <circle className="eill-flake-5" cx="0" cy="0" r="1.3" opacity="0.9" />
      <circle className="eill-flake-6" cx="0" cy="0" r="0.8" opacity="0.7" />
      <circle className="eill-flake-7" cx="0" cy="0" r="1.1" opacity="0.85" />
      <circle className="eill-flake-8" cx="0" cy="0" r="0.9" opacity="0.75" />
    </g>
    <rect width="350" height="200" filter="url(#xm-grain)" opacity="0.32" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// VALENTINE'S — two overlapping hearts, risograph overprint.
// ────────────────────────────────────────────────────────────────────────────
const EIllValentines = () => (
  <svg className="eill eill-valentines" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="vl-grain" light seed={13} strength={0.2} />
      <clipPath id="vl-heart-clip">
        <path d="M 180 74 C 168 58, 140 60, 140 84 C 140 108, 180 138, 180 138 C 180 138, 220 108, 220 84 C 220 60, 192 58, 180 74 Z" />
      </clipPath>
    </defs>
    <rect width="350" height="200" fill="#F4E4D4" />
    <g style={{ mixBlendMode: "multiply" }}>
      <path d="M 210 70 C 196 52, 166 54, 166 80 C 166 108, 210 142, 210 142 C 210 142, 254 108, 254 80 C 254 54, 224 52, 210 70 Z" fill="#A02A38" opacity="0.92" />
    </g>
    <g style={{ mixBlendMode: "multiply" }}>
      <path d="M 180 74 C 168 58, 140 60, 140 84 C 140 108, 180 138, 180 138 C 180 138, 220 108, 220 84 C 220 60, 192 58, 180 74 Z" fill="#EC6F52" opacity="0.9" />
      <g clipPath="url(#vl-heart-clip)" className="eill-vl-sheen">
        <ellipse cx="156" cy="90" rx="10" ry="18" fill="#FBD9CE" opacity="0.7" transform="rotate(-18 156 90)" />
      </g>
    </g>
    <rect width="350" height="200" filter="url(#vl-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// NEW YEAR — city silhouette under midnight sky, single firework burst.
// ────────────────────────────────────────────────────────────────────────────
const EIllNewYear = () => (
  <svg className="eill eill-newyear" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="ny-grain" seed={17} strength={0.28} />
      <radialGradient id="ny-sky" cx="70%" cy="30%" r="65%">
        <stop offset="0%" stopColor="#2A3660" />
        <stop offset="100%" stopColor="#121830" />
      </radialGradient>
    </defs>
    <rect width="350" height="200" fill="url(#ny-sky)" />
    <g fill="#0A0E1F" opacity="0.9">
      <rect x="0" y="168" width="350" height="32" />
      <rect x="20" y="156" width="14" height="14" />
      <rect x="42" y="148" width="20" height="22" />
      <rect x="72" y="158" width="10" height="12" />
      <rect x="92" y="146" width="16" height="24" />
      <rect x="118" y="152" width="10" height="18" />
      <rect x="140" y="138" width="24" height="32" />
      <rect x="175" y="150" width="12" height="20" />
      <rect x="200" y="132" width="20" height="38" />
      <rect x="232" y="148" width="14" height="22" />
      <rect x="258" y="158" width="10" height="12" />
      <rect x="278" y="144" width="22" height="26" />
      <rect x="308" y="154" width="12" height="16" />
      <rect x="328" y="148" width="18" height="22" />
    </g>
    <g fill="#E4B45C" opacity="0.75">
      <rect x="48" y="154" width="2" height="2" />
      <rect x="56" y="162" width="2" height="2" />
      <rect x="148" y="146" width="2" height="2" />
      <rect x="156" y="156" width="2" height="2" />
      <rect x="205" y="140" width="2" height="2" />
      <rect x="213" y="150" width="2" height="2" />
      <rect x="284" y="152" width="2" height="2" />
      <rect x="336" y="154" width="2" height="2" />
    </g>
    <g fill="#E8DCC2" opacity="0.7">
      <circle cx="40" cy="28" r="1" />
      <circle cx="80" cy="60" r="0.8" />
      <circle cx="305" cy="40" r="1" />
      <circle cx="195" cy="24" r="0.9" />
      <circle cx="125" cy="48" r="0.7" />
    </g>
    <g transform="translate(248 60)">
      <g className="eill-burst">
        <circle cx="0" cy="0" r="2.5" fill="#FFF4D6" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30) * Math.PI / 180;
          return (
            <line key={i} x1="0" y1="0"
                  x2={Math.cos(a) * 34} y2={Math.sin(a) * 34}
                  stroke="#F2C870" strokeWidth="1.3" strokeLinecap="round" />
          );
        })}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 + 15) * Math.PI / 180;
          return (
            <line key={`s${i}`} x1="0" y1="0"
                  x2={Math.cos(a) * 22} y2={Math.sin(a) * 22}
                  stroke="#E4B45C" strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
          );
        })}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30) * Math.PI / 180;
          return <circle key={`t${i}`} cx={Math.cos(a) * 34} cy={Math.sin(a) * 34} r="1.2" fill="#FFF4D6" />;
        })}
      </g>
    </g>
    <rect width="350" height="200" filter="url(#ny-grain)" opacity="0.32" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// ANNIVERSARY — two interlocking gold rings, diamond on top edge.
// ────────────────────────────────────────────────────────────────────────────
const EIllAnniversary = () => (
  <svg className="eill eill-anniversary" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="an-grain" seed={23} strength={0.24} />
      <radialGradient id="an-vignette" cx="50%" cy="55%" r="65%">
        <stop offset="50%" stopColor="#3A0D1E" stopOpacity="0" />
        <stop offset="100%" stopColor="#1E0611" stopOpacity="0.8" />
      </radialGradient>
    </defs>
    <rect width="350" height="200" fill="#5A1528" />
    <rect width="350" height="200" fill="url(#an-vignette)" />
    <circle cx="58" cy="60" r="1.2" fill="#E4B45C" opacity="0.4" />
    <circle cx="310" cy="150" r="1" fill="#E4B45C" opacity="0.4" />
    <circle cx="42" cy="148" r="1" fill="#E4B45C" opacity="0.3" />
    <g transform="translate(150 108) rotate(-10)">
      <ellipse cx="0" cy="0" rx="38" ry="36" fill="none" stroke="#B88520" strokeWidth="6" />
      <ellipse cx="0" cy="0" rx="32" ry="30" fill="none" stroke="#8F6418" strokeWidth="0.8" opacity="0.6" />
      <ellipse cx="0" cy="0" rx="38" ry="36" fill="none" stroke="#F2D78A" strokeWidth="0.8" opacity="0.4" />
    </g>
    <g transform="translate(208 106) rotate(6)">
      <ellipse cx="0" cy="0" rx="38" ry="36" fill="none" stroke="#D4A24A" strokeWidth="6" />
      <ellipse cx="0" cy="0" rx="32" ry="30" fill="none" stroke="#F2D78A" strokeWidth="0.8" opacity="0.55" />
      <g className="eill-anniv-diamond">
        <path d="M -5 -38 L 5 -38 L 4 -44 L -4 -44 Z" fill="#C9951E" />
        <line x1="-3" y1="-44" x2="-4" y2="-48" stroke="#C9951E" strokeWidth="1" strokeLinecap="round" />
        <line x1="3" y1="-44" x2="4" y2="-48" stroke="#C9951E" strokeWidth="1" strokeLinecap="round" />
        <g transform="translate(0 -50)">
          <path d="M -5 0 L 5 0 L 3 -5 L -3 -5 Z" fill="#FBF7EA" />
          <path d="M -5 0 L 0 6 L 5 0 Z" fill="#EDE5CC" />
          <line x1="-5" y1="0" x2="-1.5" y2="-5" stroke="#B8A67E" strokeWidth="0.4" />
          <line x1="5" y1="0" x2="1.5" y2="-5" stroke="#B8A67E" strokeWidth="0.4" />
          <line x1="-1.5" y1="-5" x2="0" y2="0" stroke="#B8A67E" strokeWidth="0.4" opacity="0.6" />
          <line x1="1.5" y1="-5" x2="0" y2="0" stroke="#B8A67E" strokeWidth="0.4" opacity="0.6" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="#B8A67E" strokeWidth="0.4" opacity="0.6" />
          <rect x="-0.6" y="-3.8" width="1.2" height="1.2" fill="#FFFFFF" className="eill-anniv-sparkle" />
        </g>
      </g>
    </g>
    <rect width="350" height="200" filter="url(#an-grain)" opacity="0.38" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// WEDDING — three-bloom bouquet with ribbon tie, falling petal.
// ────────────────────────────────────────────────────────────────────────────
const EIllWedding = () => (
  <svg className="eill eill-wedding" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="wd-grain" light seed={29} strength={0.16} /></defs>
    <rect width="350" height="200" fill="#F3EDDF" />
    <ellipse cx="175" cy="88" rx="120" ry="82" fill="#F4E1E0" opacity="0.55" />
    <g>
      <path d="M 175 184 C 174 158, 176 124, 175 82" fill="none" stroke="#7A9265" strokeWidth="3" strokeLinecap="round" />
      <path d="M 170 184 C 160 160, 144 134, 128 94" fill="none" stroke="#7A9265" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M 180 184 C 196 160, 216 130, 228 96" fill="none" stroke="#7A9265" strokeWidth="2.6" strokeLinecap="round" />
    </g>
    <path d="M 160 140 C 138 138, 122 122, 118 108 C 138 114, 154 126, 164 138 Z" fill="#93AC7C" />
    <path d="M 198 128 C 220 126, 232 112, 236 98 C 220 102, 206 114, 200 124 Z" fill="#AFC598" />
    <g>
      <circle cx="175" cy="74" r="30" fill="#FAE5E6" />
      <path d="M 175 44 C 192 48, 200 60, 200 74 C 192 64, 184 58, 172 52 Z" fill="#F4D9DB" />
      <path d="M 145 74 C 148 60, 158 50, 172 48 C 164 58, 156 70, 148 80 Z" fill="#F4D9DB" />
      <path d="M 175 104 C 162 98, 150 90, 148 76 C 158 84, 168 92, 176 96 Z" fill="#F4D9DB" />
      <path d="M 205 74 C 202 88, 192 98, 178 102 C 186 92, 196 84, 202 74 Z" fill="#F4D9DB" />
      <circle cx="175" cy="74" r="12" fill="#FFFFFF" opacity="0.55" />
      <circle cx="175" cy="74" r="5" fill="#E8B64A" />
      <circle cx="174" cy="73" r="1.8" fill="#FFF4D6" />
    </g>
    <g>
      <circle cx="128" cy="94" r="20" fill="#F4C9D0" />
      <path d="M 128 76 C 138 80, 144 86, 142 94 C 136 88, 130 82, 128 78 Z" fill="#E8A8B5" />
      <path d="M 110 94 C 114 86, 122 80, 126 78 C 122 86, 116 92, 112 98 Z" fill="#E8A8B5" />
      <path d="M 146 94 C 144 102, 136 108, 128 110 C 134 104, 140 98, 144 96 Z" fill="#E8A8B5" />
      <circle cx="128" cy="94" r="6" fill="#F4D9DB" />
      <circle cx="128" cy="94" r="2.2" fill="#D48796" />
    </g>
    <g>
      <circle cx="228" cy="96" r="17" fill="#F4C9D0" />
      <path d="M 228 80 C 238 84, 242 90, 240 96 C 234 90, 230 84, 228 82 Z" fill="#E8A8B5" />
      <path d="M 212 96 C 216 88, 222 84, 226 82 C 222 88, 218 94, 214 100 Z" fill="#E8A8B5" />
      <path d="M 244 96 C 242 104, 236 108, 228 110 C 234 104, 240 98, 244 96 Z" fill="#E8A8B5" />
      <circle cx="228" cy="96" r="5" fill="#F4D9DB" />
      <circle cx="228" cy="96" r="1.8" fill="#D48796" />
    </g>
    <g>
      <path d="M 166 184 C 166 178, 184 178, 184 184 C 186 192, 184 200, 175 202 C 166 200, 164 192, 166 184 Z" fill="#C9A26B" />
      <path d="M 168 200 C 156 210, 150 224, 148 240 C 158 230, 164 222, 172 212 Z" fill="#C9A26B" />
      <path d="M 182 200 C 194 210, 202 222, 204 240 C 196 230, 188 222, 180 212 Z" fill="#D4B07A" />
    </g>
    <g className="eill-wd-petal">
      <path d="M 0 0 C -5 -3, -5 -9, 0 -12 C 5 -9, 5 -3, 0 0 Z" fill="#F4C9D0" opacity="0.95" />
    </g>
    <rect width="350" height="200" filter="url(#wd-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// JUST BECAUSE — Matisse-style sun disc with rotating rays, low horizon.
// ────────────────────────────────────────────────────────────────────────────
const EIllJustBecause = () => (
  <svg className="eill eill-justbecause" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="jb-grain" light seed={31} strength={0.2} /></defs>
    <rect width="350" height="200" fill="#E8C987" />
    <path d="M 0 168 C 50 164, 100 172, 160 166 C 220 160, 270 170, 350 166 L 350 200 L 0 200 Z" fill="#8C3A1A" opacity="0.88" />
    <g className="eill-rays" style={{ transformOrigin: "140px 100px", transformBox: "view-box" }}>
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * 22.5) * Math.PI / 180;
        const r1 = 56;
        const r2 = i % 2 === 0 ? 92 : 78;
        const x1 = 140 + Math.cos(a) * r1;
        const y1 = 100 + Math.sin(a) * r1;
        const x2 = 140 + Math.cos(a) * r2;
        const y2 = 100 + Math.sin(a) * r2;
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#A84A1E" strokeWidth={i % 2 === 0 ? 2.2 : 1.4} strokeLinecap="round" opacity="0.9" />
        );
      })}
    </g>
    <g>
      <circle cx="140" cy="100" r="48" fill="#E87F3C" />
      <circle cx="140" cy="100" r="48" fill="none" stroke="#8C3A1A" strokeWidth="0.8" opacity="0.3" />
      <path d="M 108 80 C 108 62, 130 58, 150 68 C 130 72, 116 82, 108 94 Z" fill="#F2A03E" opacity="0.8" />
    </g>
    <rect width="350" height="200" filter="url(#jb-grain)" opacity="0.45" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// HALLOWEEN — Jack-o'-lantern, pulsing ground glow, partial moon.
// ────────────────────────────────────────────────────────────────────────────
const EIllHalloween = () => (
  <svg className="eill eill-halloween" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="hal-grain" seed={4} strength={0.25} />
      <radialGradient id="hal-glow" cx="50%" cy="62%" r="40%">
        <stop offset="0%"   stopColor="#F2A63B" stopOpacity="0.55" />
        <stop offset="45%"  stopColor="#D97A1E" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#D97A1E" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="hal-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#2A1E2E" />
        <stop offset="100%" stopColor="#1A1420" />
      </linearGradient>
    </defs>
    <rect width="350" height="200" fill="url(#hal-sky)" />
    <ellipse cx="175" cy="140" rx="150" ry="60" fill="url(#hal-glow)" className="eill-glow" />
    <circle cx="340" cy="48" r="22" fill="#F3E7C9" opacity="0.85" />
    <circle cx="333" cy="43" r="4" fill="#D4C49E" opacity="0.6" />
    <circle cx="346" cy="56" r="3" fill="#D4C49E" opacity="0.5" />
    <g>
      <path d="M 125 105 C 105 108, 100 130, 105 150 C 112 168, 130 174, 150 172 L 150 105 Z" fill="#B8561A" />
      <path d="M 225 105 C 245 108, 250 130, 245 150 C 238 168, 220 174, 200 172 L 200 105 Z" fill="#B8561A" />
      <path d="M 145 100 C 120 102, 110 125, 115 148 C 123 170, 155 178, 175 176 C 195 178, 227 170, 235 148 C 240 125, 230 102, 205 100 C 195 99, 185 102, 175 103 C 165 102, 155 99, 145 100 Z" fill="#D97A1E" />
      <path d="M 150 106 C 148 140, 154 170, 160 175" fill="none" stroke="#A5511A" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <path d="M 200 106 C 202 140, 196 170, 190 175" fill="none" stroke="#A5511A" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <path d="M 175 100 C 173 135, 177 168, 175 176" fill="none" stroke="#A5511A" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
    </g>
    <path d="M 170 100 C 168 88, 172 80, 178 78 C 185 76, 188 82, 186 90 C 185 96, 180 100, 175 100 Z" fill="#4A5033" />
    <path d="M 175 90 L 178 82" stroke="#2F3421" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
    <g className="eill-carved">
      <path d="M 148 128 L 161 128 L 155 140 Z" fill="#FFD47A" />
      <path d="M 148 128 L 161 128 L 155 140 Z" fill="none" stroke="#FFB347" strokeWidth="1" opacity="0.5" />
      <path d="M 189 128 L 202 128 L 195 140 Z" fill="#FFD47A" />
      <path d="M 189 128 L 202 128 L 195 140 Z" fill="none" stroke="#FFB347" strokeWidth="1" opacity="0.5" />
      <path d="M 172 142 L 178 142 L 175 150 Z" fill="#FFD47A" />
      <path d="M 152 155 L 167 158 L 170 152 L 175 158 L 180 152 L 183 158 L 198 155 L 195 163 L 155 163 Z" fill="#FFD47A" />
    </g>
    <rect width="350" height="200" filter="url(#hal-grain)" opacity="0.35" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// SPRING (blossom) — cherry-blossom branch entering from upper-right.
// ────────────────────────────────────────────────────────────────────────────
const EIllBlossom = () => (
  <svg className="eill eill-blossom" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="bl-grain" light seed={7} strength={0.18} /></defs>
    <rect width="350" height="200" fill="#F4EBD9" />
    <g>
      <path d="M 350 20 C 320 28, 290 40, 260 55 C 240 65, 220 72, 200 85" fill="none" stroke="#6B7544" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M 290 48 C 285 58, 288 70, 295 82" fill="none" stroke="#6B7544" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M 240 68 C 238 80, 232 92, 225 104" fill="none" stroke="#6B7544" strokeWidth="2" strokeLinecap="round" />
    </g>
    <g>
      <Blossom x={305} y={32} s={1.5} />
      <Blossom x={275} y={52} s={1.25} />
      <Blossom x={295} y={78} s={1.0} />
      <Blossom x={230} y={72} s={1.35} />
      <Blossom x={220} y={105} s={1.15} />
      <Blossom x={255} y={58} s={0.85} />
    </g>
    <g className="eill-petal-fall">
      <path d="M 0 0 C -6 -3, -6 -10, 0 -14 C 6 -10, 6 -3, 0 0 Z" fill="#DA8FA1" opacity="0.9" />
    </g>
    <g opacity="0.55">
      <circle cx="48" cy="178" r="3.5" fill="#DA8FA1" />
      <circle cx="48" cy="178" r="1.8" fill="#B36676" />
    </g>
    <rect width="350" height="200" filter="url(#bl-grain)" opacity="0.6" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// AUTUMN — bare tree silhouette, ochre sky, three tumbling leaves.
// ────────────────────────────────────────────────────────────────────────────
const EIllAutumn = () => (
  <svg className="eill eill-autumn" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <Grain id="au-grain" light seed={7} strength={0.18} />
      <linearGradient id="au-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#E8C987" />
        <stop offset="100%" stopColor="#DBA85A" />
      </linearGradient>
    </defs>
    <rect width="350" height="200" fill="url(#au-sky)" />
    <rect y="155" width="350" height="45" fill="#C48A3F" opacity="0.3" />
    <g>
      <path d="M 85 200 L 82 175 C 82 160, 85 145, 88 125 C 90 110, 92 95, 94 80 C 95 70, 93 62, 95 55" fill="none" stroke="#3A2A1E" strokeWidth="4.5" strokeLinecap="round" />
      <path d="M 92 90 C 75 80, 62 72, 50 62" fill="none" stroke="#3A2A1E" strokeWidth="2.8" strokeLinecap="round" />
      <path d="M 93 75 C 108 70, 120 66, 135 58" fill="none" stroke="#3A2A1E" strokeWidth="2.8" strokeLinecap="round" />
      <path d="M 95 60 C 85 45, 78 35, 72 25" fill="none" stroke="#3A2A1E" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M 95 55 C 110 48, 120 40, 128 32" fill="none" stroke="#3A2A1E" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M 96 50 C 105 40, 112 30, 118 18" fill="none" stroke="#3A2A1E" strokeWidth="2" strokeLinecap="round" />
      <path d="M 62 67 C 55 60, 50 55, 42 50" fill="none" stroke="#3A2A1E" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 118 64 C 126 60, 132 55, 140 48" fill="none" stroke="#3A2A1E" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 82 38 C 75 30, 72 23, 68 16" fill="none" stroke="#3A2A1E" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 110 40 C 115 32, 118 25, 120 18" fill="none" stroke="#3A2A1E" strokeWidth="1.5" strokeLinecap="round" />
    </g>
    <Leaf x={42} y={50} rot={-20} color="#B85C23" s={0.7} />
    <Leaf x={135} y={58} rot={30} color="#D9843A" s={0.75} />
    <Leaf x={68} y={16} rot={-35} color="#C9A235" s={0.65} />
    <g className="eill-leaf-fall eill-leaf-1">
      <Leaf x={0} y={0} rot={0} color="#B85C23" s={1} />
    </g>
    <g className="eill-leaf-fall eill-leaf-2">
      <Leaf x={0} y={0} rot={0} color="#D9843A" s={0.85} />
    </g>
    <g className="eill-leaf-fall eill-leaf-3">
      <Leaf x={0} y={0} rot={0} color="#C9A235" s={0.75} />
    </g>
    <rect width="350" height="200" filter="url(#au-grain)" opacity="0.5" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// SIGNATURE — neutral fallback for any unknown occasion.
// ────────────────────────────────────────────────────────────────────────────
const EIllSignature = () => (
  <svg className="eill eill-signature" viewBox="0 0 350 200" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs><Grain id="sig-grain" light seed={2} strength={0.18} /></defs>
    <rect width="350" height="200" fill="#EDE6D3" />
    <g>
      <circle cx="245" cy="105" r="55" fill="#C9A26B" opacity="0.9" />
      <circle cx="245" cy="105" r="55" fill="none" stroke="#6B5536" strokeWidth="1.2" opacity="0.5" strokeDasharray="1 3" />
    </g>
    <circle cx="305" cy="58" r="6" fill="#6B5536" opacity="0.7" />
    <rect width="350" height="200" filter="url(#sig-grain)" opacity="0.55" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// Switcher — map occasion id → illustration component. Unknown ids fall
// through to the Signature scene.
// ────────────────────────────────────────────────────────────────────────────
const SCENES = {
  birthday:    EIllBirthday,
  mothersday:  EIllMothers,
  thankyou:    EIllThanks,
  christmas:   EIllChristmas,
  holiday:     EIllChristmas,
  valentines:  EIllValentines,
  newyear:     EIllNewYear,
  anniversary: EIllAnniversary,
  wedding:     EIllWedding,
  justbecause: EIllJustBecause,
  halloween:   EIllHalloween,
  blossom:     EIllBlossom,
  autumn:      EIllAutumn,
};

const EditorialScene = ({ occasion }) => {
  const Scene = SCENES[occasion] || EIllSignature;
  return <Scene />;
};

export default EditorialScene;
